"use client";

import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { useParams, useNavigate } from "react-router-dom";
import { fetchDomeById, updateDome, fetchTimezones } from "../../api/dome.api";
import { useJsApiLoader } from "@react-google-maps/api";

const libraries = ["places"];

const EditDome = () => {
  const { domeid: id } = useParams();
  const navigate = useNavigate();

  const [domeData, setDomeData] = useState({
    domeId: "",
    domeName: "",
    domeDescription: "",
    price: "",
    location: "",
    coordinatesLat: "",
    coordinatesLng: "",
    timezone: "",
  });

  const [existingImages, setExistingImages] = useState([]);
  const [newImages, setNewImages] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);

  const [coverImage, setCoverImage] = useState(null); // for existing cover image URL
  const [newCoverImage, setNewCoverImage] = useState(null); // for new uploaded cover file
  const [coverPreview, setCoverPreview] = useState(null); // preview URL for new cover

  const [timezones, setTimezones] = useState([]);

  useEffect(() => {
    fetchTimezones().then(setTimezones);
  }, []);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: libraries,
  });

  const autocompleteInputRef = useRef(null);

  /* ---------------- Fetch Dome Data ---------------- */
  useEffect(() => {
    const loadDome = async () => {
      try {
        setLoading(true);
        const data = await fetchDomeById(id);
        setDomeData({
          domeId: data.domeId || "",
          domeName: data.domeName || "",
          domeDescription: data.domeDescription || "",
          price: data.price || "",
          // offerPercent: data.offerPercent || 0,
          location: data.location || "",
          coordinatesLat: data.coordinates?.lat || "",
          coordinatesLng: data.coordinates?.lng || "",
          timezone: data.timezone || "",
        });
        setExistingImages(data.images || []);
        setCoverImage(data.coverImage || null);
      } catch (err) {
        console.error(err);
        setError("Failed to fetch dome details");
      } finally {
        setLoading(false);
      }
    };
    loadDome();
  }, [id]);

  /* ---------------- Google Places Autocomplete ---------------- */
  const placeAutocompleteRef = useRef(null);

  useEffect(() => {
    // Only proceed if API is loaded and dome data is ready
    if (!isLoaded || loading || !placeAutocompleteRef.current) return;

    // Check if we already have the autocomplete element inside this mount
    if (placeAutocompleteRef.current.querySelector("gmp-place-autocomplete")) return;

    const initAutocomplete = async () => {
      try {
        console.log("🛠️ [EditDome] Initializing Google Places Web Component...");
        const { PlaceAutocompleteElement } = await window.google.maps.importLibrary("places");

        // Final check before adding to avoid double-mounts
        if (placeAutocompleteRef.current.querySelector("gmp-place-autocomplete")) return;

        const autocompleteElement = new PlaceAutocompleteElement();
        autocompleteElement.id = "place-autocomplete-input";
        placeAutocompleteRef.current.appendChild(autocompleteElement);

        autocompleteElement.addEventListener("gmp-places-select", async (event) => {
          const place = event?.detail?.place;
          if (!place) return;

          try {
            await place.fetchFields({ fields: ["displayName", "formattedAddress", "location"] });
            const address = place.formattedAddress || place.displayName || "";
            const lat = place.location?.lat?.();
            const lng = place.location?.lng?.();

            autocompleteElement.value = address;
            setDomeData((prev) => ({
              ...prev,
              location: address,
              coordinatesLat: lat,
              coordinatesLng: lng,
            }));
          } catch (e) {
            console.error("Error fetching place fields:", e);
          }
        });
      } catch (e) {
        console.error("❌ Failed to load Places API:", e);
      }
    };

    // Small delay to ensure the DOM ref is stable during navigation transitions
    const timer = setTimeout(initAutocomplete, 100);
    return () => clearTimeout(timer);
  }, [isLoaded, loading]);

  /* ---------------- Handlers ---------------- */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setDomeData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const oversized = files.filter(f => f.size > 5 * 1024 * 1024);
    if (oversized.length > 0) {
      toast.error(`${oversized.length} image(s) exceed 5MB and were skipped.`);
    }
    const valid = files.filter(f => f.size <= 5 * 1024 * 1024);
    setNewImages((prev) => [...prev, ...valid]);
  };

  const handleRemoveExistingImage = (url) => {
    setExistingImages((prev) => prev.filter((img) => img !== url));
  };

  const handleRemoveNewImage = (index) => {
    setNewImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleCoverChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Cover image is too large. Please upload a file smaller than 5MB.');
        e.target.value = '';
        return;
      }
      setNewCoverImage(file);
      setCoverPreview(URL.createObjectURL(file));
    }
  };

  /* ---------------- Preview URLs ---------------- */
  useEffect(() => {
    const urls = newImages.map((file) => ({
      file,
      url: URL.createObjectURL(file),
    }));
    setPreviewUrls(urls);

    return () => urls.forEach(({ url }) => URL.revokeObjectURL(url));
  }, [newImages]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      if (!coverImage && !newCoverImage) {
        setError("Cover image is mandatory. Please provide one.");
        setLoading(false);
        return;
      }

      if (existingImages.length === 0 && newImages.length === 0) {
        setError("At least one additional image is mandatory.");
        setLoading(false);
        return;
      }

      const formData = new FormData();

      // Ensure we have a non-empty location value, prioritizing the actual widget text
      const placeInput = document.getElementById("place-autocomplete-input");
      const placeValue = placeInput?.value?.trim();

      // If the user has typed/selected something in the widget, use that.
      // Otherwise, fall back to the existing domeData.location.
      let locationToSend = placeValue || domeData.location?.trim() || "";

      if (!locationToSend) {
        setError("Please search and select a location.");
        setLoading(false);
        return;
      }

      // Explicitly set the location to send
      formData.append("location", locationToSend);

      // ✅ Add all other dome fields
      Object.entries(domeData).forEach(([key, value]) => {
        if (key === "location") return; // handled above

        // Skip empty price if it was commented out/not provided 
        if (key === "price" && (value === "" || value === null)) return;

        // For coordinates, only send if we have a valid numeric value
        if (
          (key === "coordinatesLat" || key === "coordinatesLng") &&
          (value === "" || value === null || Number.isNaN(Number(value)))
        ) {
          return;
        }

        formData.append(key, value);
      });

      // ✅ Handle existing image URLs (if any)
      if (existingImages && existingImages.length > 0) {
        existingImages.forEach((url) => {
          formData.append("images", url); // Keep as 'existingImages' for clarity
        });
      }

      // ✅ Include new uploaded files (go to req.files.images)
      newImages.forEach((file) => formData.append("images", file));

      // ✅ Cover image handling (prioritize new upload)
      if (newCoverImage) {
        formData.append("coverImage", newCoverImage);
      } else if (coverImage) {
        formData.append("coverImage", coverImage);
      }

      // ✅ Debugging output
      for (const [key, value] of formData.entries()) {
        console.log(`${key}:`, value);
      }

      await updateDome(id, formData);
      navigate(-1);
    } catch (err) {
      console.error(err);
      setError(err?.message || "Failed to update dome");
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="container mt-4">
      <div className="d-flex align-items-center mb-4">
        <button
          onClick={() => navigate(-1)}
          className="btn btn-outline-secondary me-3"
        >
          <i className="fas fa-arrow-left me-2"></i>Back
        </button>
        <h2 className="mb-0">
          Edit Dome:{" "}
          <span className="text-primary">
            {domeData.domeName || "Loading..."}
          </span>
        </h2>
      </div>

      {loading || !isLoaded ? (
        <p>Loading dome details...</p>
      ) : (
        <form onSubmit={handleSubmit} className="mt-3">
          {/* Dome ID */}
          <div className="mb-3">
            <label className="form-label">Dome ID</label>
            <input
              type="text"
              name="domeId"
              value={domeData.domeId}
              onChange={handleChange}
              className="form-control"
              required
            />
          </div>

          {/* Dome Name */}
          <div className="mb-3">
            <label className="form-label">Dome Name</label>
            <input
              type="text"
              name="domeName"
              value={domeData.domeName}
              onChange={handleChange}
              className="form-control"
              required
            />
          </div>

          {/* Dome Description */}
          <div className="mb-3">
            <label className="form-label">Dome Description</label>
            <textarea
              name="domeDescription"
              value={domeData.domeDescription}
              onChange={handleChange}
              className="form-control"
              rows={3}
            />
          </div>

          {/* Price & Offer */}
          <div className="row">
            {/* <div className="col-md-6 mb-3">
              <label className="form-label">Price</label>
              <input
                type="number"
                name="price"
                value={domeData.price}
                onChange={handleChange}
                className="form-control"
                min={0}
                required
              />
            </div> */}

            {/* <div className="col-md-6 mb-3">
              <label className="form-label">Offer Percent</label>
              <input
                type="number"
                name="offerPercent"
                value={domeData.offerPercent}
                onChange={handleChange}
                className="form-control"
                min={0}
                max={100}
              />
            </div> */}
          </div>

          {/* Location */}
          <div className="mb-3">
            <label className="form-label">Location (Search & Select to update)</label>
            <div ref={placeAutocompleteRef} style={{ minHeight: '40px' }} className="mb-2"></div>
            {domeData.location && (
              <div className="text-success small mb-2">
                <strong>Current:</strong> {domeData.location}
              </div>
            )}
            <input
              type="text"
              name="location"
              value={domeData.location}
              readOnly
              className="form-control d-none"
              required
            />
            <div className="form-text">Search and select from the dropdown to update the dome's location and timezone.</div>
          </div>

          {/* Timezone */}
          <div className="mb-3">
            <label className="form-label">Timezone</label>
            <select
              name="timezone"
              value={domeData.timezone}
              onChange={handleChange}
              className="form-control"
              required
            >
              <option value="">-- Select Timezone --</option>
              {timezones.map((tz) => (
                <option key={tz} value={tz}>
                  {tz}
                </option>
              ))}
            </select>
            <div className="form-text">
              Choose the physical timezone where the dome is located.
            </div>
          </div>

          {/* Cover Image */}
          <div className="mb-3">
            <label className="form-label">Cover Image</label>
            {coverPreview ? (
              <img
                src={coverPreview}
                alt="Cover Preview"
                className="img-fluid mb-2 rounded"
                style={{ maxHeight: 200 }}
              />
            ) : coverImage ? (
              <img
                src={coverImage}
                alt="Existing Cover"
                className="img-fluid mb-2 rounded"
                style={{ maxHeight: 200 }}
              />
            ) : null}
            <input
              type="file"
              accept="image/*"
              onChange={handleCoverChange}
              className="form-control"
            />
            <div className="form-text">Accepted formats: JPG, PNG, WEBP &middot; Max size: 5MB</div>
          </div>

          {/* Existing Images */}
          {existingImages.length > 0 && (
            <div className="mb-3">
              <label className="form-label">Existing Images</label>
              <div className="mt-2 d-flex flex-wrap gap-3">
                {existingImages.map((url, idx) => (
                  <div
                    key={idx}
                    className="border rounded position-relative overflow-hidden"
                    style={{
                      width: "120px",
                      height: "100px",
                      backgroundColor: "#f8f9fa",
                    }}
                  >
                    <img
                      src={url}
                      alt={`existing-${idx}`}
                      className="img-fluid w-100 h-100"
                      style={{ objectFit: "cover" }}
                    />
                    <button
                      type="button"
                      className="btn btn-sm btn-danger position-absolute top-0 end-0 m-1 px-2 py-0"
                      style={{
                        borderRadius: "50%",
                        fontSize: "0.8rem",
                        lineHeight: "1rem",
                      }}
                      onClick={() => handleRemoveExistingImage(url)}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Upload New Images */}
          <div className="mb-3">
            <label className="form-label">Add New Images</label>
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={handleFileChange}
              className="form-control"
            />
            <div className="form-text">Accepted formats: JPG, PNG, WEBP &middot; Max 10 images &middot; Max size: 5MB per image</div>
            {previewUrls.length > 0 && (
              <div className="mt-3 d-flex flex-wrap gap-3">
                {previewUrls.map(({ url }, idx) => (
                  <div
                    key={idx}
                    className="border rounded position-relative overflow-hidden"
                    style={{
                      width: "120px",
                      height: "100px",
                      backgroundColor: "#f8f9fa",
                    }}
                  >
                    <img
                      src={url}
                      alt={`preview-${idx}`}
                      className="img-fluid w-100 h-100"
                      style={{ objectFit: "cover" }}
                    />
                    <button
                      type="button"
                      className="btn btn-sm btn-danger position-absolute top-0 end-0 m-1 px-2 py-0"
                      style={{
                        borderRadius: "50%",
                        fontSize: "0.8rem",
                        lineHeight: "1rem",
                      }}
                      onClick={() => handleRemoveNewImage(idx)}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {error && <div className="alert alert-danger">{error}</div>}

          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? "Updating..." : "Update Dome"}
          </button>
        </form>
      )}
    </div>
  );
};

export default EditDome;
